import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  HStack,
  IconButton,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  Tooltip,
  Wrap,
  WrapItem,
  useToast,
} from '@chakra-ui/react'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import { Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Link2, Eraser, Paperclip } from 'lucide-react'
import { useThemeColors } from '@/hooks/useThemeColors'
import {
  EMAIL_ATTACHMENT_MAX_FILES,
  isEmptyHtml,
  mergeEmailAttachments,
} from '@/lib/utils/emailAttachments'

type Props = {
  value: string
  onChange: (html: string) => void
  attachments: File[]
  onAttachmentsChange: (files: File[]) => void
  placeholder?: string
  minHeight?: string
  isDisabled?: boolean
  showAttachments?: boolean
}

function ToolbarButton({
  label,
  isActive,
  onClick,
  children,
  isDisabled,
}: {
  label: string
  isActive?: boolean
  onClick: () => void
  children: React.ReactNode
  isDisabled?: boolean
}) {
  return (
    <Tooltip label={label} hasArrow>
      <IconButton
        aria-label={label}
        size="xs"
        variant={isActive ? 'solid' : 'ghost'}
        colorScheme={isActive ? 'blue' : 'gray'}
        onClick={onClick}
        isDisabled={isDisabled}
        icon={<>{children}</>}
      />
    </Tooltip>
  )
}

export function EmailRichComposer({
  value,
  onChange,
  attachments,
  onAttachmentsChange,
  placeholder = 'Type your message…',
  minHeight = '180px',
  isDisabled = false,
  showAttachments = true,
}: Props) {
  const colors = useThemeColors()
  const toast = useToast()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [charCount, setCharCount] = useState(0)

  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
    ],
    content: value || '',
    editable: !isDisabled,
    onUpdate: ({ editor: current }) => {
      const html = current.getHTML()
      onChange(html)
      setCharCount(current.getText().length)
    },
    editorProps: {
      attributes: {
        class: 'email-rich-composer-prose',
        'data-placeholder': placeholder,
      },
    },
  })

  useEffect(() => {
    if (!editor) return
    editor.setEditable(!isDisabled)
  }, [editor, isDisabled])

  // Sync external clears (e.g. after send) without fighting typing.
  useEffect(() => {
    if (!editor) return
    const current = editor.getHTML()
    if (value === current) return
    if (isEmptyHtml(value) && isEmptyHtml(current)) return
    editor.commands.setContent(value || '', { emitUpdate: false })
    setCharCount(editor.getText().length)
  }, [editor, value])

  const setLink = useCallback(() => {
    if (!editor) return
    const previous = editor.getAttributes('link').href as string | undefined
    const url = window.prompt('Link URL', previous || 'https://')
    if (url === null) return
    const trimmed = url.trim()
    if (trimmed === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: trimmed }).run()
  }, [editor])

  const onPickFiles = (list: FileList | null) => {
    if (!list || list.length === 0) return
    const { files, error } = mergeEmailAttachments(attachments, list)
    onAttachmentsChange(files)
    if (error) {
      toast({ status: 'warning', title: error, duration: 4000, isClosable: true })
    }
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const removeAttachment = (index: number) => {
    onAttachmentsChange(attachments.filter((_, i) => i !== index))
  }

  return (
    <Box>
      <HStack
        spacing={1}
        mb={1}
        flexWrap="wrap"
        borderWidth="1px"
        borderColor={colors.inputBorder}
        borderBottomWidth={0}
        borderTopRadius="md"
        px={2}
        py={1}
        bg={colors.inputBg}
      >
        <ToolbarButton
          label="Bold"
          isActive={editor?.isActive('bold')}
          isDisabled={isDisabled || !editor}
          onClick={() => editor?.chain().focus().toggleBold().run()}
        >
          <Bold size={14} />
        </ToolbarButton>
        <ToolbarButton
          label="Italic"
          isActive={editor?.isActive('italic')}
          isDisabled={isDisabled || !editor}
          onClick={() => editor?.chain().focus().toggleItalic().run()}
        >
          <Italic size={14} />
        </ToolbarButton>
        <ToolbarButton
          label="Underline"
          isActive={editor?.isActive('underline')}
          isDisabled={isDisabled || !editor}
          onClick={() => editor?.chain().focus().toggleUnderline().run()}
        >
          <UnderlineIcon size={14} />
        </ToolbarButton>
        <ToolbarButton
          label="Bullet list"
          isActive={editor?.isActive('bulletList')}
          isDisabled={isDisabled || !editor}
          onClick={() => editor?.chain().focus().toggleBulletList().run()}
        >
          <List size={14} />
        </ToolbarButton>
        <ToolbarButton
          label="Numbered list"
          isActive={editor?.isActive('orderedList')}
          isDisabled={isDisabled || !editor}
          onClick={() => editor?.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered size={14} />
        </ToolbarButton>
        <ToolbarButton label="Link" isActive={editor?.isActive('link')} isDisabled={isDisabled || !editor} onClick={setLink}>
          <Link2 size={14} />
        </ToolbarButton>
        <ToolbarButton
          label="Clear formatting"
          isDisabled={isDisabled || !editor}
          onClick={() => editor?.chain().focus().clearNodes().unsetAllMarks().run()}
        >
          <Eraser size={14} />
        </ToolbarButton>
      </HStack>

      <Box
        borderWidth="1px"
        borderColor={colors.inputBorder}
        borderBottomRadius={showAttachments ? 'none' : 'md'}
        bg={colors.inputBg}
        minH={minHeight}
        px={3}
        py={2}
        sx={{
          '.email-rich-composer-prose': {
            outline: 'none',
            minHeight,
            fontSize: 'sm',
            color: colors.text,
          },
          '.email-rich-composer-prose p': { margin: '0 0 0.5em' },
          '.email-rich-composer-prose ul, .email-rich-composer-prose ol': {
            paddingLeft: '1.25rem',
            marginBottom: '0.5em',
          },
          '.email-rich-composer-prose a': { color: colors.brandBlue, textDecoration: 'underline' },
          '.email-rich-composer-prose.ProseMirror-focused': { outline: 'none' },
        }}
      >
        <EditorContent editor={editor} />
      </Box>

      {showAttachments ? (
        <Box
          borderWidth="1px"
          borderTopWidth={0}
          borderColor={colors.inputBorder}
          borderBottomRadius="md"
          px={2}
          py={2}
          bg={colors.inputBg}
        >
          <HStack justify="space-between" mb={attachments.length ? 2 : 0}>
            <Button
              size="xs"
              leftIcon={<Paperclip size={14} />}
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              isDisabled={isDisabled || attachments.length >= EMAIL_ATTACHMENT_MAX_FILES}
            >
              Attach files
            </Button>
            <Text fontSize="xs" color="gray.500">
              {charCount} {charCount === 1 ? 'character' : 'characters'}
              {attachments.length > 0 ? ` · ${attachments.length}/${EMAIL_ATTACHMENT_MAX_FILES} files` : ''}
            </Text>
          </HStack>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.txt,.csv,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip"
            onChange={(e) => onPickFiles(e.target.files)}
          />
          {attachments.length > 0 ? (
            <Wrap spacing={2}>
              {attachments.map((file, index) => (
                <WrapItem key={`${file.name}-${file.size}-${index}`}>
                  <Tag size="sm" borderRadius="full" variant="subtle" colorScheme="blue">
                    <TagLabel maxW="180px" isTruncated title={file.name}>
                      {file.name}
                    </TagLabel>
                    <TagCloseButton isDisabled={isDisabled} onClick={() => removeAttachment(index)} />
                  </Tag>
                </WrapItem>
              ))}
            </Wrap>
          ) : null}
        </Box>
      ) : (
        <Text fontSize="xs" color="gray.500" mt={1} textAlign="right">
          {charCount} {charCount === 1 ? 'character' : 'characters'}
        </Text>
      )}
    </Box>
  )
}

export { isEmptyHtml }
